// CRM: lifecycle stages, derived from the data. Pure; see crmTypes.ts and docs/crm-design.md.
//
// Child stage, first rule that applies:
//   aged out of Holiday Club (and not at preschool)  -> alumni
//   at preschool now                                  -> active
//   registered, start date ahead                      -> registered
//   holiday booking upcoming or in the last 12 months -> holidayOnly
//   holiday bookings, but older                       -> lapsed
//   left preschool                                    -> alumni
//   registered but never started                      -> lapsed
//   otherwise (only an enquiry)                       -> enquiry
// A family takes the most engaged stage of its children (STAGE_PRIORITY), unless the manager overrides it.

import type { Identity, PreschoolStatus, Stage } from "./crmTypes";
import { addMonths, monthYear, schoolStart, schoolStartYear } from "./crmNormalise";

export interface LifecycleAssumptions {
  /** No booking for this long and the family counts as lapsed. */
  lapseMonths: number;
  /** A registration without a child record older than this (and no future start date) never started. */
  registrationValidMonths: number;
}

export const STAGE_PRIORITY: Stage[] = ["active", "registered", "holidayOnly", "enquiry", "lapsed", "alumni"];

/** Holiday Club eligibility ends on 31 August after Year 6 (age 11), seven years after starting school. */
export const holidayUntil = (dateOfBirth: string) => `${schoolStartYear(dateOfBirth) + 7}-08-31`;

export const preschoolStatusOf = (
  identities: Identity[],
  dateOfBirth: string | null,
  today: string,
  a: LifecycleAssumptions
): { status: PreschoolStatus; reason: string } => {
  let best: { status: PreschoolStatus; reason: string } = { status: "none", reason: "" };
  const rank: PreschoolStatus[] = ["active", "registered", "left", "noShow", "none"];
  const consider = (status: PreschoolStatus, reason: string) => {
    if (rank.indexOf(status) < rank.indexOf(best.status)) best = { status, reason };
  };
  for (const identity of identities) {
    const p = identity.preschool;
    if (!p) continue;
    const start = p.startDate;
    if (identity.kind === "preschoolChild") {
      if (p.left) consider("left", "Left preschool");
      else if (dateOfBirth && today >= schoolStart(dateOfBirth)) consider("left", `Started school ${monthYear(schoolStart(dateOfBirth))}`);
      else if (start && start > today) consider("registered", `Starts ${monthYear(start)}`);
      else consider("active", "At preschool");
    } else if (identity.kind === "preschoolRegistration") {
      const recent = identity.at ? identity.at >= addMonths(today, -a.registrationValidMonths) : false;
      if (start && start >= today) consider("registered", `Starts ${monthYear(start)}`);
      else if (!start && recent) consider("registered", "Registered, no start date");
      else consider("noShow", "Registered but never started");
    } else if (identity.kind === "invoiceOnly") {
      consider("left", "Past preschool invoices only");
    }
  }
  return best;
};

export interface HolidayFacts {
  bookings: number;
  first: string | null;
  last: string | null;
  upcoming: boolean;
}

/** Bookings that went ahead (not cancelled, at least one day), with the first and last day. */
export const holidayFactsOf = (identities: Identity[], today: string): HolidayFacts => {
  let bookings = 0;
  let first: string | null = null;
  let last: string | null = null;
  for (const identity of identities) {
    if (identity.service !== "holiday") continue;
    const h = identity.holiday;
    if (h && (h.cancelled || h.fullDays + h.halfDays === 0)) continue;
    const start = h?.firstDay ?? identity.at;
    const end = h?.lastDay ?? identity.at;
    bookings++;
    if (start && (!first || start < first)) first = start;
    if (end && (!last || end > last)) last = end;
  }
  return { bookings, first, last, upcoming: !!last && last >= today };
};

export const childStageOf = (
  preschool: { status: PreschoolStatus; reason: string },
  holiday: HolidayFacts,
  dateOfBirth: string | null,
  today: string,
  a: LifecycleAssumptions
): { stage: Stage; reason: string } => {
  const atPreschool = preschool.status === "active" || preschool.status === "registered";
  if (!atPreschool && dateOfBirth && today > holidayUntil(dateOfBirth)) {
    return { stage: "alumni", reason: "Aged out of Holiday Club" };
  }
  if (preschool.status === "active") return { stage: "active", reason: preschool.reason };
  if (preschool.status === "registered") return { stage: "registered", reason: preschool.reason };
  const lapseFrom = addMonths(today, -a.lapseMonths);
  if (holiday.upcoming) return { stage: "holidayOnly", reason: "Holiday Club booking coming up" };
  if (holiday.last && holiday.last >= lapseFrom) return { stage: "holidayOnly", reason: `Holiday Club ${monthYear(holiday.last)}` };
  if (holiday.bookings > 0) {
    return { stage: "lapsed", reason: holiday.last ? `No booking since ${monthYear(holiday.last)}` : "No recent booking" };
  }
  if (preschool.status === "left") return { stage: "alumni", reason: preschool.reason };
  if (preschool.status === "noShow") return { stage: "lapsed", reason: preschool.reason };
  return { stage: "enquiry", reason: "Enquiry" };
};

/** The family's stage: the most engaged child's. A family without children is an enquiry or lapsed. */
export const familyStageOf = (children: { stage: Stage; reason: string }[], hasEnquiry: boolean): { stage: Stage; reason: string } => {
  const best = [...children].sort((x, y) => STAGE_PRIORITY.indexOf(x.stage) - STAGE_PRIORITY.indexOf(y.stage))[0];
  if (best) return best;
  return hasEnquiry ? { stage: "enquiry", reason: "Enquiry" } : { stage: "lapsed", reason: "No children on record" };
};
